// Derives the money states the guest payment page renders: what is being
// charged now, what is left afterwards, and which copy describes it. Kept
// separate from the component so each state can be asserted directly.

const toAmount = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Cents are the smallest unit any of these figures can carry, so rounding to
// cents keeps a float subtraction such as 363.8 - 63.8 from leaving a residual
// balance that would wrongly suppress the settles-booking copy.
const roundToCents = amount => Math.round(amount * 100) / 100;

const chargeAmountDueNow = paymentState =>
  Math.max(
    0,
    roundToCents(toAmount(paymentState && paymentState.bookingPaymentDueToday))
  );

const remainingBalance = booking => {
  if (!booking) return 0;
  const remaining =
    booking.price_remaining !== undefined && booking.price_remaining !== null
      ? toAmount(booking.price_remaining)
      : toAmount(booking.price_total) - toAmount(booking.price_paid);
  return Math.max(0, roundToCents(remaining));
};

const balanceAfterCharge = (booking, chargeAmount) =>
  Math.max(0, roundToCents(remainingBalance(booking) - toAmount(chargeAmount)));

const isFullyPaid = booking =>
  Boolean(booking) && remainingBalance(booking) <= 0;

// A charge is only the final payment when it leaves nothing behind, which is
// what licenses the "final payment" wording the old copy used unconditionally.
const chargeSettlesBooking = (booking, chargeAmount) =>
  toAmount(chargeAmount) > 0 && balanceAfterCharge(booking, chargeAmount) <= 0;

// Returns the translation key (and values) for the secondary balance line, or
// null when there is no balance left to describe.
const remainingBalanceNotice = ({
  amount,
  dueWhenText,
  autoCollected,
  formatAmount = String
}) => {
  const balance = Math.max(0, roundToCents(toAmount(amount)));
  if (balance <= 0) return null;
  const dated = Boolean(dueWhenText);
  const keys = {
    'true,true': `cx.payment.remaining_balance_auto_collected`,
    'true,false': `cx.payment.remaining_balance_due`,
    'false,true': `cx.payment.remaining_balance_auto_collected_later`,
    'false,false': `cx.payment.remaining_balance_due_later`
  };
  const values = { amount: formatAmount(balance) };
  if (dated) values.date = dueWhenText;
  return { key: keys[`${dated},${Boolean(autoCollected)}`], values };
};

module.exports = {
  balanceAfterCharge,
  chargeAmountDueNow,
  chargeSettlesBooking,
  isFullyPaid,
  remainingBalance,
  remainingBalanceNotice
};
